import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calculator, TrendingDown, Award, Mail, Phone, Building2 } from "lucide-react";
import SEO from "../components/SEO";
import "./CorporationTaxCalculator.css";

export const CorporationTaxCalculator = () => {
  // A/B Test Variant Management
  const [variant, setVariant] = useState(null);
  
  // Calculator Inputs
  const [profit, setProfit] = useState("");
  const [associatedCompanies, setAssociatedCompanies] = useState(1);
  const [unclaimedExpenses, setUnclaimedExpenses] = useState("");
  const [assetPurchases, setAssetPurchases] = useState("");
  const [pensionContribution, setPensionContribution] = useState("");
  const [rdSpend, setRdSpend] = useState("");
  const [extraction, setExtraction] = useState("mixed");
  
  // Results State
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  
  // Form States
  const [leadFormData, setLeadFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    profitRange: ""
  });
  
  const [emailCaptureData, setEmailCaptureData] = useState({
    name: "",
    email: ""
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // A/B Test Variant Assignment (on mount)
  useEffect(() => {
    // Check session storage for existing variant
    const storedVariant = sessionStorage.getItem('taxCalcVariant');
    
    if (storedVariant) {
      setVariant(storedVariant);
    } else {
      // Randomly assign variant
      const variants = ['variant_A', 'variant_B', 'variant_C'];
      const randomVariant = variants[Math.floor(Math.random() * variants.length)];
      setVariant(randomVariant);
      sessionStorage.setItem('taxCalcVariant', randomVariant);
    }
  }, []);

  // Calculate Corporation Tax
  const calculateTax = (profitAmount) => {
    const threshold1 = 50000 / associatedCompanies;
    const threshold2 = 250000 / associatedCompanies;
    
    if (profitAmount <= 0) return 0;
    
    if (profitAmount <= threshold1) {
      return profitAmount * 0.19;
    } else if (profitAmount >= threshold2) {
      return profitAmount * 0.25;
    } else {
      // Marginal relief zone - blended rate ~25%
      const effectiveRate = 0.19 + ((profitAmount - threshold1) / (threshold2 - threshold1)) * 0.06;
      return profitAmount * effectiveRate;
    }
  };

  // Handle Calculator Submission
  const handleCalculate = (e) => {
    e.preventDefault();
    
    const profitNum = parseFloat(profit) || 0;
    const expensesNum = parseFloat(unclaimedExpenses) || 0;
    const assetsNum = parseFloat(assetPurchases) || 0;
    const pensionNum = parseFloat(pensionContribution) || 0;
    const rdNum = parseFloat(rdSpend) || 0;
    
    // Calculate current tax
    const currentTax = calculateTax(profitNum);
    
    // Calculate adjusted profit
    const rdBenefit = rdNum * 0.20; // 20% R&D benefit
    const adjustedProfit = Math.max(0, profitNum - expensesNum - pensionNum - assetsNum - rdBenefit);
    
    // Calculate tax after planning
    const taxAfterPlanning = calculateTax(adjustedProfit);
    
    // Calculate saving
    const saving = Math.max(0, currentTax - taxAfterPlanning);
    
    // Calculate effective rates
    const currentRate = profitNum > 0 ? (currentTax / profitNum) * 100 : 0;
    const newRate = adjustedProfit > 0 ? (taxAfterPlanning / adjustedProfit) * 100 : 0;
    
    setResults({
      currentTax: Math.round(currentTax),
      taxAfterPlanning: Math.round(taxAfterPlanning),
      saving: Math.round(saving),
      currentRate: currentRate.toFixed(1),
      newRate: newRate.toFixed(1),
      breakdown: {
        expenses: expensesNum > 0 ? Math.round(calculateTax(expensesNum)) : 0,
        pension: pensionNum > 0 ? Math.round(calculateTax(pensionNum)) : 0,
        assets: assetsNum > 0 ? Math.round(calculateTax(assetsNum)) : 0,
        rd: rdNum > 0 ? Math.round(rdBenefit * (profitNum > 0 ? 0.19 : 0.25)) : 0
      }
    });
    
    setShowResults(true);
    
    // Track calculator completion
    if (window.gtag) {
      window.gtag('event', 'calculator_completion', {
        event_category: 'engagement',
        event_label: variant,
        value: Math.round(saving)
      });
    }
    
    // Scroll to results
    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Handle Lead Form Submission
  const handleLeadSubmit = (e) => {
    e.preventDefault();
    
    // Track lead form submission
    if (window.gtag) {
      window.gtag('event', 'lead_form_submission', {
        event_category: 'conversion',
        event_label: variant,
        value: results?.saving || 0
      });
    }
    
    console.log('Lead Form Data:', leadFormData);
    setFormSubmitted(true);
  };

  // Handle Email Capture
  const handleEmailCapture = async (e) => {
    e.preventDefault();
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/leads/save-estimate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: emailCaptureData.name,
          email: emailCaptureData.email,
          estimated_saving: results?.saving || 0
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setEmailSent(true);
        
        // Track email capture
        if (window.gtag) {
          window.gtag('event', 'email_capture', {
            event_category: 'engagement',
            event_label: variant,
            value: results?.saving || 0
          });
        }
      }
    } catch (error) {
      console.error('Email capture error:', error);
      alert('Unable to send estimate. Please try again.');
    }
  };

  // Get variant-specific message
  const getVariantMessage = () => {
    if (!results || results.saving === 0) return null;
    
    const savingFormatted = `£${results.saving.toLocaleString()}`;
    
    switch(variant) {
      case 'variant_A':
        return `You could save up to ${savingFormatted} in Corporation Tax.`;
      case 'variant_B':
        return `You may be overpaying up to ${savingFormatted} in Corporation Tax.`;
      case 'variant_C':
        return `You could be losing ${savingFormatted} each year in unnecessary tax.`;
      default:
        return `You could save up to ${savingFormatted} in Corporation Tax.`;
    }
  };

  return (
    <div className="calculator-page">
      <SEO
        title="Corporation Tax Calculator UK | Estimate Your Tax Saving"
        description="Free Corporation Tax calculator for UK limited companies. Estimate your potential tax savings and identify legal tax-saving opportunities."
        canonical="/corporation-tax-calculator"
        keywords="corporation tax calculator UK, tax saving calculator, corporation tax planning, reduce corporation tax"
      />

      {/* Hero Section */}
      <section className="calc-hero">
        <div className="container">
          <div className="calc-hero-content">
            <div className="calc-insight-banner" data-testid="calc-insight-banner">
              <span className="calc-insight-accent" aria-hidden="true"></span>
              <div className="calc-insight-text">
                <p className="calc-insight-headline">
                  Most UK business owners miss simple tax-saving opportunities.
                </p>
                <p className="calc-insight-subtext">
                  Use this calculator to estimate how much Corporation Tax you could potentially save.
                </p>
              </div>
            </div>
            <h1 className="calc-hero-title">Estimate How Much Corporation Tax You Could Save</h1>
            <p className="calc-hero-subtitle">Identify legal tax-saving opportunities in minutes</p>
            <div className="calc-hero-badge">
              <Calculator size={24} />
              <span>Free • Instant • No Registration Required</span>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="calc-section">
        <div className="container">
          <div className="calc-wrapper">
            <div className="calc-card">
              <h2 className="calc-card-title">Your Company Details</h2>
              
              <form onSubmit={handleCalculate} className="calc-form">
                <div className="calc-form-group">
                  <label htmlFor="profit">Annual Profit (before tax)</label>
                  <div className="calc-input-wrapper">
                    <span className="calc-input-prefix">£</span>
                    <input
                      type="number"
                      id="profit"
                      value={profit}
                      onChange={(e) => setProfit(e.target.value)}
                      placeholder="Enter amount"
                      required
                      min="0"
                      step="1000"
                    />
                  </div>
                </div>

                <div className="calc-form-group">
                  <label htmlFor="companies">Number of Associated Companies</label>
                  <select
                    id="companies"
                    value={associatedCompanies}
                    onChange={(e) => setAssociatedCompanies(parseInt(e.target.value))}
                  >
                    <option value="1">1 (just this company)</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5+</option>
                  </select>
                </div>

                <div className="calc-form-group">
                  <label htmlFor="expenses">Unclaimed Business Expenses</label>
                  <div className="calc-input-wrapper">
                    <span className="calc-input-prefix">£</span>
                    <input
                      type="number"
                      id="expenses"
                      value={unclaimedExpenses}
                      onChange={(e) => setUnclaimedExpenses(e.target.value)}
                      placeholder="Enter amount"
                      min="0"
                      step="500"
                    />
                  </div>
                  <span className="calc-input-hint">Office costs, travel, software not yet claimed</span>
                </div>

                <div className="calc-form-group">
                  <label htmlFor="assets">Planned Asset Purchases</label>
                  <div className="calc-input-wrapper">
                    <span className="calc-input-prefix">£</span>
                    <input
                      type="number"
                      id="assets"
                      value={assetPurchases}
                      onChange={(e) => setAssetPurchases(e.target.value)}
                      placeholder="Enter amount"
                      min="0"
                      step="1000"
                    />
                  </div>
                  <span className="calc-input-hint">Equipment, furniture, machinery</span>
                </div>

                <div className="calc-form-group">
                  <label htmlFor="pension">Employer Pension Contributions</label>
                  <div className="calc-input-wrapper">
                    <span className="calc-input-prefix">£</span>
                    <input
                      type="number"
                      id="pension"
                      value={pensionContribution}
                      onChange={(e) => setPensionContribution(e.target.value)}
                      placeholder="Enter amount"
                      min="0"
                      step="500"
                    />
                  </div>
                </div>

                <div className="calc-form-group">
                  <label htmlFor="rd">R&D Spend (optional)</label>
                  <div className="calc-input-wrapper">
                    <span className="calc-input-prefix">£</span>
                    <input
                      type="number"
                      id="rd"
                      value={rdSpend}
                      onChange={(e) => setRdSpend(e.target.value)}
                      placeholder="Enter amount"
                      min="0"
                      step="1000"
                    />
                  </div>
                  <span className="calc-input-hint">Innovation, product development costs</span>
                </div>

                <div className="calc-form-group">
                  <label htmlFor="extraction">Income Extraction Method</label>
                  <select
                    id="extraction"
                    value={extraction}
                    onChange={(e) => setExtraction(e.target.value)}
                  >
                    <option value="mixed">Salary + Dividends (recommended)</option>
                    <option value="salary">Salary only</option>
                    <option value="dividends">Dividends only</option>
                  </select>
                </div>

                <button type="submit" className="calc-submit-btn">
                  <Calculator size={20} />
                  Calculate My Savings
                </button>
              </form>
            </div>

            {/* Trust Badges */}
            <div className="calc-trust">
              <div className="trust-badge">
                <Award size={20} />
                <span>FCCA Qualified</span>
              </div>
              <div className="trust-badge">
                <TrendingDown size={20} />
                <span>15+ Years Experience</span>
              </div>
              <div className="trust-badge">
                <Building2 size={20} />
                <span>UK Tax Specialists</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      {showResults && results && (
        <section id="results-section" className="calc-results-section">
          <div className="container">
            <div className="calc-results-wrapper">
              
              {/* Variant Message */}
              {results.saving > 0 && (
                <div className="variant-message">
                  <h2>{getVariantMessage()}</h2>
                  <p>Based on your inputs, there may be opportunities to reduce your tax bill legally.</p>
                </div>
              )}

              {/* Results Cards */}
              <div className="results-grid">
                <div className="result-card">
                  <span className="result-label">Estimated Tax Now</span>
                  <span className="result-value">£{results.currentTax.toLocaleString()}</span>
                  <span className="result-rate">{results.currentRate}% effective rate</span>
                </div>

                <div className="result-card">
                  <span className="result-label">Estimated Tax After Planning</span>
                  <span className="result-value result-value-success">£{results.taxAfterPlanning.toLocaleString()}</span>
                  <span className="result-rate">{results.newRate}% effective rate</span>
                </div>

                <div className="result-card result-card-highlight">
                  <span className="result-label">Estimated Saving</span>
                  <span className="result-value result-value-large">£{results.saving.toLocaleString()}</span>
                  <span className="result-rate">
                    {results.saving > 0 ? 'Potential annual reduction' : 'Already optimized'}
                  </span>
                </div>
              </div>

              {/* Breakdown */}
              {results.saving > 0 && (
                <div className="results-breakdown">
                  <h3>Breakdown of Potential Savings</h3>
                  <div className="breakdown-items">
                    {results.breakdown.expenses > 0 && (
                      <div className="breakdown-item">
                        <span>Unclaimed Expenses</span>
                        <span>£{results.breakdown.expenses.toLocaleString()}</span>
                      </div>
                    )}
                    {results.breakdown.pension > 0 && (
                      <div className="breakdown-item">
                        <span>Pension Contributions</span>
                        <span>£{results.breakdown.pension.toLocaleString()}</span>
                      </div>
                    )}
                    {results.breakdown.assets > 0 && (
                      <div className="breakdown-item">
                        <span>Capital Allowances</span>
                        <span>£{results.breakdown.assets.toLocaleString()}</span>
                      </div>
                    )}
                    {results.breakdown.rd > 0 && (
                      <div className="breakdown-item">
                        <span>R&D Tax Relief</span>
                        <span>£{results.breakdown.rd.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Re-engagement hook */}
              {results.saving > 0 && (
                <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.9rem', color: '#555' }}>
                  Not sure if this is accurate? Most clients we work with save more after a proper review.
                </p>
              )}

              {/* Associated Companies Warning */}
              {associatedCompanies > 1 && (
                <div className="calc-warning">
                  <strong>⚠️ Multiple Companies:</strong> Tax thresholds (£50,000 and £250,000) are split between 
                  your {associatedCompanies} associated companies, which may increase your effective tax rate.
                </div>
              )}

              {/* Disclaimer */}
              <div className="calc-disclaimer">
                <strong>Important:</strong> Estimates only – simplified assumptions. Actual savings depend on your 
                specific circumstances, allowable expenses, and tax planning strategies. Consult a qualified accountant 
                for accurate advice.
              </div>

              {/* Micro-conversion text */}
              <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.95rem', fontStyle: 'italic', color: '#666' }}>
                Most clients we work with didn't realise they were overpaying until they ran this calculation.
              </p>

              {/* Post-Results CTA */}
              {results.saving > 0 && (
                <div className="lead-capture-section" style={{ marginTop: '2rem' }}>
                  <div className="lead-capture-card">
                    <h2>🚀 You could be overpaying up to £{results.saving.toLocaleString()} in Corporation Tax</h2>
                    <p style={{ fontSize: '1rem', marginTop: '1rem', marginBottom: '1rem' }}>
                      This is an estimate based on your inputs. Most clients in this situation prefer a quick call to confirm exact savings.
                    </p>
                    <Link 
                      to="/contact#calendar" 
                      className="lead-submit-btn" 
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', textDecoration: 'none' }}
                      onClick={() => {
                        // Track contact page visit
                        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/contact-visit`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            source: 'calculator_post_results',
                            estimated_saving: results.saving
                          })
                        }).catch(err => console.log('Analytics tracking failed'));
                      }}
                    >
                      <Phone size={20} />
                      Book a Free Tax Review
                    </Link>
                  </div>
                </div>
              )}

              {/* Lead Capture Form */}
              <div className="lead-capture-section">
                <div className="lead-capture-card">
                  {!formSubmitted ? (
                    <>
                      <h2>🚀 Ready to Reduce Your Tax Bill?</h2>
                      <p>
                        You could be overpaying up to £{results.saving.toLocaleString()} in Corporation Tax.
                      </p>
                      <p style={{ marginTop: '0.5rem', fontSize: '0.95rem' }}>
                        This is an estimate. We can confirm exact savings.
                      </p>
                      
                      <form onSubmit={handleLeadSubmit} className="lead-form">
                        <div className="lead-form-grid">
                          <input
                            type="text"
                            placeholder="Your Name"
                            value={leadFormData.name}
                            onChange={(e) => setLeadFormData({...leadFormData, name: e.target.value})}
                            required
                          />
                          <input
                            type="email"
                            placeholder="Email Address"
                            value={leadFormData.email}
                            onChange={(e) => setLeadFormData({...leadFormData, email: e.target.value})}
                            required
                          />
                          <input
                            type="tel"
                            placeholder="Phone Number"
                            value={leadFormData.phone}
                            onChange={(e) => setLeadFormData({...leadFormData, phone: e.target.value})}
                            required
                          />
                          <input
                            type="text"
                            placeholder="Company Name"
                            value={leadFormData.company}
                            onChange={(e) => setLeadFormData({...leadFormData, company: e.target.value})}
                            required
                          />
                          <select
                            value={leadFormData.profitRange}
                            onChange={(e) => setLeadFormData({...leadFormData, profitRange: e.target.value})}
                            required
                          >
                            <option value="">Annual Profit Range</option>
                            <option value="0-50k">£0 - £50,000</option>
                            <option value="50k-100k">£50,000 - £100,000</option>
                            <option value="100k-250k">£100,000 - £250,000</option>
                            <option value="250k+">£250,000+</option>
                          </select>
                        </div>
                        
                        <button type="submit" className="lead-submit-btn">
                          <Phone size={20} />
                          Book a Free Tax Review
                        </button>
                      </form>
                    </>
                  ) : (
                    <div className="form-success">
                      <h3>✅ Thank You!</h3>
                      <p>
                        Thanks — we'll review your details within 24 hours.
                      </p>
                      <p style={{ marginTop: '1rem', fontSize: '0.95rem' }}>
                        If your query is urgent, you can book a call now.
                      </p>
                      <Link 
                        to="/contact" 
                        className="lead-submit-btn" 
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', textDecoration: 'none' }}
                        onClick={() => {
                          // Track contact page visit
                          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/contact-visit`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              source: 'calculator_post_submission',
                              estimated_saving: results?.saving || 0
                            })
                          }).catch(err => console.log('Analytics tracking failed'));
                        }}
                      >
                        <Phone size={20} />
                        👉 Book a Call Now
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Email Capture */}
              {!emailSent && (
                <div className="email-capture-section">
                  <h3>Save Your Estimate</h3>
                  <p>Receive your tax saving estimate via email</p>
                  
                  <form onSubmit={handleEmailCapture} className="email-capture-form">
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={emailCaptureData.name}
                      onChange={(e) => setEmailCaptureData({...emailCaptureData, name: e.target.value})}
                      required
                    />
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={emailCaptureData.email}
                      onChange={(e) => setEmailCaptureData({...emailCaptureData, email: e.target.value})}
                      required
                    />
                    <button type="submit" className="email-capture-btn">
                      <Mail size={18} />
                      Send My Estimate
                    </button>
                  </form>
                </div>
              )}

              {emailSent && (
                <div className="email-success">
                  ✅ Estimate sent! Check your inbox.
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Trust Section */}
      <section className="calc-trust-section">
        <div className="container">
          <h2>Why Brightson Accounting?</h2>
          <div className="trust-grid">
            <div className="trust-item">
              <Award size={40} />
              <h3>FCCA Qualified</h3>
              <p>Chartered Certified Accountants with advanced UK tax expertise</p>
            </div>
            <div className="trust-item">
              <TrendingDown size={40} />
              <h3>15+ Years Experience</h3>
              <p>Proven track record helping Wolverhampton businesses reduce tax</p>
            </div>
            <div className="trust-item">
              <Building2 size={40} />
              <h3>Wolverhampton Focus</h3>
              <p>Local specialists understanding West Midlands business challenges</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporationTaxCalculator;
